import {
  SortBy,
  SortCriterion,
  SortOrder,
  buildQueryExpression,
} from '@coveo/atomic-react';
import { SaleType } from '@lib/types/Sale';
import { SaleFilterOption } from '@lib/types/cms/DynamicProductListing';
import { format } from 'date-fns';

/*
 * Using filters set in the Contentful CMS, build a Coveo query expression to filter the search results.
 * Coveo syntax query reference: https://docs.coveo.com/en/1552/searching-with-coveo/query-syntax#field-queries
 */
export const buildQueryExpressionWithCMSInputs = ({
  categories,
  priceMax,
  priceMin,
  publishers,
  rating,
  sales,
  unityVersion,
  publishedAt,
  filterByDiscount,
  tag,
}: {
  categories?: string;
  priceMax?: number;
  priceMin?: number;
  publishers?: string;
  rating?: number;
  sales?: SaleFilterOption[];
  unityVersion?: string;
  publishedAt?: Date;
  filterByDiscount?: number;
  tag?: string;
}): string => {
  const queryExpression = buildQueryExpression();

  if (categories) {
    // coveo expects subcategories to be separated by ' > '
    const categoriesArray =
      categories
        .split(',')
        .map((category) => category.replaceAll('/', ' > ').trim()) ?? [];
    queryExpression.addStringField({
      field: 'ec_category',
      operator: 'isExactly',
      values: categoriesArray,
    });
  }

  if (priceMin) {
    queryExpression.addNumericField({
      field: 'ec_price_filter',
      operator: 'greaterThanOrEqual',
      value: priceMin,
    });
  }

  if (priceMax || priceMax === 0) {
    queryExpression.addNumericField({
      field: 'ec_price_filter',
      operator: 'lowerThanOrEqual',
      value: priceMax,
    });
  }

  if (publishers) {
    const publishersArray =
      publishers
        ?.split(',')
        .map((publisherIdString) => publisherIdString.trim()) ?? [];
    queryExpression.addStringField({
      field: 'publisher_id',
      operator: 'isExactly',
      values: publishersArray,
    });
  }

  if (rating) {
    const MAX_VALUE = 5;
    const FIVE_STAR_LOWER_BOUND = 4.75;
    const ratingWithSoftMax =
      rating === MAX_VALUE ? FIVE_STAR_LOWER_BOUND : rating;

    queryExpression.addNumericField({
      field: 'ec_rating',
      operator: 'greaterThanOrEqual',
      value: ratingWithSoftMax,
    });
  }

  if (sales && sales.length > 0) {
    const saleTypes = sales.map((sale) => {
      switch (sale) {
        case 'New Release Discount':
          return SaleType.NewReleaseDiscount;
        case 'Flash Deal':
          return 'flash_deal';
        case 'On Sale':
        default:
          return SaleType.Sale;
      }
    });
    queryExpression.addStringField({
      field: 'ec_sale_filters',
      operator: 'isExactly',
      values: saleTypes,
    });
  }

  if (unityVersion) {
    const END_OF_UNITY_VERSION = '99';
    const unityVersionNumber = parseInt(
      unityVersion.concat(END_OF_UNITY_VERSION),
      10
    );
    queryExpression.addNumericRangeField({
      field: 'min_unity_version',
      from: 0,
      to: unityVersionNumber,
    });
  }

  if (publishedAt) {
    queryExpression.addDateField({
      field: 'first_published_at',
      operator: 'greaterThanOrEqual',
      value: format(publishedAt, 'yyyy/MM/dd'),
    });
  }

  if (filterByDiscount) {
    queryExpression.addNumericField({
      field: 'ec_sale_discount_percentage_filter',
      operator: 'greaterThanOrEqual',
      /**
       * In the CMS we accept values of 0-100 to make this easier for content managers
       * So we divide it by 100 here to make the number compatible with the Coveo index
       * where the discount percentage filter is a float between 0-1.
       */
      value: filterByDiscount / 100,
    });
  }

  if (tag) {
    queryExpression.addStringField({
      field: 'ec_tags',
      operator: 'isExactly',
      values: [tag],
    });
  }

  return queryExpression.toQuerySyntax();
};

const BEST_SELLING_SORT_CRITERION = (timeFrame: string) => ({
  by: SortBy.Field,
  field: `ec_best_selling_score_${timeFrame}`,
  order: SortOrder.Descending,
});

export const convertCMSSortOrderToCoveoCriteria = (
  sortOrder: string
): SortCriterion | SortCriterion[] => {
  switch (sortOrder) {
    case 'Rating':
      return [
        {
          by: SortBy.Field,
          field: 'ec_rating_sort',
          order: SortOrder.Descending,
        },
      ];
    case 'Published Date':
      return {
        by: SortBy.Field,
        field: 'first_published_at',
        order: SortOrder.Descending,
      };
    case 'Recently Updated':
      return {
        by: SortBy.Field,
        field: 'last_published_at',
        order: SortOrder.Descending,
      };
    case 'Price (Low to High)':
      return {
        by: SortBy.Field,
        field: 'ec_price_filter',
        order: SortOrder.Ascending,
      };
    case 'Price (High to Low)':
      return {
        by: SortBy.Field,
        field: 'ec_price_filter',
        order: SortOrder.Descending,
      };
    case 'Discount Percentage':
      return {
        by: SortBy.Field,
        field: 'ec_sale_discount_percentage_filter',
        order: SortOrder.Descending,
      };
    case 'Bestselling - Last Day':
      return BEST_SELLING_SORT_CRITERION('last_day');
    case 'Bestselling - Last Month':
      return BEST_SELLING_SORT_CRITERION('last_month');
    case 'Bestselling - Last 6 Months':
      return BEST_SELLING_SORT_CRITERION('last_six_months');
    case 'Bestselling - Last Year':
      return BEST_SELLING_SORT_CRITERION('last_year');
    case 'Bestselling - Last 3 Years':
      return BEST_SELLING_SORT_CRITERION('last_three_years');
    case 'Relevance':
    case 'Popularity':
    default:
      return {
        by: SortBy.Relevancy,
      };
  }
};
